// ChatGPT Ads Measurement Pixel helpers.
// https://developers.openai.com/ads/measurement-pixel
// https://developers.openai.com/ads/supported-events
//
// The loader + oaiq("init") snippet lives in index.html. These helpers fire the
// OpenAI equivalents of our GTM dataLayer ecommerce events, next to each push.
// Amounts are integers in cents (USD minor unit), as the pixel requires.

const CURRENCY = "USD";

// Each standard event requires a data object whose `type` is its data shape.
const EVENT_DATA_TYPES = {
    page_viewed: "contents",
    contents_viewed: "contents",
    items_added: "contents",
    checkout_started: "contents",
    order_created: "contents",
    subscription_created: "plan_enrollment",
    registration_completed: "customer_action",
};

export const toCents = (dollars) => Math.round(Number(dollars || 0) * 100);

// Builds a contents[] item using only the fields the JavaScript pixel accepts.
export const openAIContent = ({ id, name, contentType = "plan", quantity, amount }) => {
    const content = { content_type: contentType };
    if (id !== undefined && id !== null && id !== "") content.id = String(id);
    if (name) content.name = String(name);
    if (quantity) content.quantity = Math.round(Number(quantity));
    if (amount !== undefined && amount !== null) {
        content.amount = Math.round(Number(amount) || 0);
        content.currency = CURRENCY;
    }
    return content;
};

export const openAIPlanContent = (plan, amount) => openAIContent({
    id: plan?.plan_id || plan?.plan_name_en,
    name: plan?.plan_name_en,
    quantity: 1,
    amount,
});

export const trackOpenAI = (eventName, { amount, planId, contents, eventId } = {}) => {
    if (typeof window === "undefined" || typeof window.oaiq !== "function") return;

    const data = { type: EVENT_DATA_TYPES[eventName] };
    if (amount !== undefined && amount !== null) {
        data.amount = Math.round(Number(amount) || 0);
        data.currency = CURRENCY;
    }
    if (planId) data.plan_id = String(planId);
    if (contents && contents.length > 0) data.contents = contents;

    const args = ["measure", eventName, data];
    if (eventId) args.push({ event_id: String(eventId) });

    // Tracking must never break the purchase flows that call it.
    try {
        window.oaiq(...args);
    } catch (error) {
        console.warn("OpenAI pixel measure failed", error);
    }
};

const sha256Hex = async (value) => {
    const bytes = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
};

// Normalization rules from the pixel docs: email is trimmed + lowercased;
// names are lowercased with whitespace and ASCII punctuation removed.
const normalizeEmail = (email) => String(email || "").trim().toLowerCase();
const normalizeName = (name) => String(name || "")
    .toLowerCase()
    .replace(/[\s!-/:-@[-`{-~]/g, "");

// Re-runs oaiq("init") with hashed user data to improve conversion matching.
// With a single pixel on the page, pixelId can be omitted after the first init.
export const identifyOpenAIUser = async (profile) => {
    if (typeof window === "undefined" || typeof window.oaiq !== "function") return;
    // crypto.subtle only exists in secure contexts (HTTPS / localhost).
    if (!window.crypto?.subtle) return;

    const fields = [
        ["email_sha256", normalizeEmail(profile?.email)],
        ["first_name_sha256", normalizeName(profile?.first_name)],
        ["last_name_sha256", normalizeName(profile?.last_name)],
    ].filter(([, value]) => value);
    if (fields.length === 0) return;

    try {
        const hashes = await Promise.all(fields.map(([, value]) => sha256Hex(value)));
        const user = Object.fromEntries(fields.map(([key], i) => [key, hashes[i]]));
        window.oaiq("init", { user });
    } catch (error) {
        console.warn("OpenAI pixel identify failed", error);
    }
};
